import { formatDateOnly } from '@/lib/format';
import type { City, EventCityMap, EventProjectMap, Project } from '@/lib/types';

/*
 * The wording and the derivations `/event-maps` runs on — the ONE copy of each.
 *
 * ⛔ **NOTHING HERE FETCHES OR WRITES.** The page owns its requests; this module
 * answers *"given what the server said, what may the screen say, and what may it
 * offer?"*. Same standing as `credit-notes.ts` and `company-projects.ts`, and
 * the same prohibition: no hook may be added, because the two tables need
 * different reads and a hook here would force them to share one.
 *
 * ⚠️ **A SECOND COPY OF ANY SENTENCE BELOW WOULD DRIFT ON A CONSEQUENCE.** Both
 * consequences describe what happens to a PUBLIC FORM the moment somebody
 * presses save — a form SEED has no other way to watch — so the failure of two
 * copies is not an inconsistency, it is one of them telling somebody the wrong
 * thing about whether submissions will still be accepted.
 */

/* -------------------------------------------------------------------------- */
/* The two consequences                                                       */
/* -------------------------------------------------------------------------- */

/**
 * What ticking `Active` off actually does.
 *
 * ⛔ **NOT HELPER TEXT, AND A SWEEP MUST NOT TAKE IT.** The no-helper-text rule
 * forbids prose that competes with a control and rots beside it; the four kinds
 * it keeps are errors, refusals, why-a-control-is-disabled, and **a destructive
 * consequence**. This is the fourth: the control is a tickbox whose effect is
 * entirely outside this app — a public form starts refusing submissions naming
 * that key — and **nothing on this screen, or on any screen, shows that
 * happening.** A reader who unticks it and is told nothing has been given no way
 * to know they have closed a door.
 *
 * ⚠️ **It states BOTH halves on purpose.** *"Stops new submissions"* alone reads
 * as data loss and stops somebody retiring a key they should; *"answers keep
 * resolving"* alone reads as harmless and invites unticking the live ones. The
 * pair is what makes it a decision rather than a guess.
 */
export const RETIRE_CONSEQUENCE =
  'While this is off, the 2027 form refuses new submissions naming this key. Answers already stored keep resolving to the same record.';

/**
 * What Delete does that retiring does not.
 *
 * ⛔ **IT NAMES THE REVERSIBLE ALTERNATIVE, AND THAT IS THE POINT OF IT.** Both
 * actions stop new submissions, so a dialog that only warned about the stopping
 * would describe the two identically and leave the irreversible one looking like
 * the tidier way to do the same job.
 */
export const DELETE_CONSEQUENCE =
  'The key stops being recognised and what it pointed at is no longer recorded anywhere. Turning the mapping off instead stops new submissions the same way and can be turned back on.';

/* -------------------------------------------------------------------------- */
/* Telling one season from the next                                           */
/* -------------------------------------------------------------------------- */

/**
 * The part of a project that is NOT its name — its cycle, its intake and when it
 * runs.
 *
 * 🔴 **THIS IS THE WHOLE ANTI-WRONG-MATCH DESIGN, AND IT EXISTS BECAUSE THE NAME
 * DOES NOT DISCRIMINATE.** `Project.name` is explicitly **not unique** — one
 * product genuinely runs twice in a period — so a 2026 festival and its 2027
 * successor differ by a word nobody reads carefully, or by nothing at all. Map
 * an `event_key` to the wrong one and every submission naming it files against
 * the wrong season, with a **201** and no way to tell afterwards.
 *
 * ⚠️ **Returns `''` when a project carries none of the three**, and the caller
 * decides what to do with that — it must NOT be padded into a plausible-looking
 * string. `cycle` and `intake` are both nullable on the read (`intake: null`
 * means the project spans the whole cycle, a real state) and both dates are
 * independently nullable. A project with nothing to distinguish it is a fact
 * about the catalogue that a person needs to see, not something to paper over.
 *
 * ⚠️ **Dates are compared and printed as STRINGS.** `new Date('2027-05-31')` is
 * midnight UTC and renders as 30 May west of Greenwich; `formatDateOnly` is the
 * house helper that does not make that mistake.
 */
export function projectSeason(project: Project): string {
  const parts: string[] = [];
  if (project.intake) parts.push(project.intake.name);
  if (project.cycle) parts.push(project.cycle.name);

  const from = project.startDate === null ? '' : formatDateOnly(project.startDate);
  const to = project.endDate === null ? '' : formatDateOnly(project.endDate);
  /*
   * All four combinations are legal and each says something different: a closed
   * period, a known start with no fixed end, a known end with no fixed start,
   * and nothing recorded. Only the last contributes no term.
   */
  if (from && to) parts.push(`${from} – ${to}`);
  else if (from) parts.push(`from ${from}`);
  else if (to) parts.push(`until ${to}`);

  return parts.join(' · ');
}

/**
 * How a project reads once it is the answer rather than one of the options.
 *
 * 🔴 **THE PICKER'S TRIGGER SHOWS `label` ALONE AND TRUNCATES IT**, so on a
 * chosen row the season — the one thing that separates two festivals — is
 * exactly what falls off the end. This is what gets rendered instead, at every
 * point a person can see a mapping: in the form under the picker, and in the
 * table cell.
 *
 * ⚠️ **It is the VALUE, not a caption about the value**, which is why it does
 * not fall foul of the no-helper-text rule any more than a truncated cell's
 * `title` does. Remove it and the screen shows a name that two records share.
 */
export function projectIdentity(project: Project): { name: string; season: string } {
  return { name: project.name, season: projectSeason(project) };
}

/**
 * What a city needs beside its name.
 *
 * ⚠️ **`country` is nullable and `null` is "no country recorded" — a real
 * state**, not a missing value to fill in, so this returns `''` and the caller
 * renders nothing rather than a guess. Two cities called Cambridge with no
 * country between them are genuinely indistinguishable here, which is a fact
 * about the master and is worth a reader seeing.
 *
 * ⛔ **`region` is deliberately NOT used.** A city's region is SEED's region of
 * focus, chosen per city and unrelated to its country — it answers a different
 * question and would read as geography beside a city name.
 */
export function citySubtitle(city: City): string {
  return city.country?.name ?? '';
}

/* -------------------------------------------------------------------------- */
/* Reading a row's own key                                                    */
/* -------------------------------------------------------------------------- */

/**
 * The key off either row shape.
 *
 * ⚠️ **The two READ types stay separate — this is a reader, not a widening.**
 * `EventProjectMap` and `EventCityMap` are deliberately not one generic, because
 * a shared `{id, key, target}` would make a city row assignable where a project
 * row is wanted and a swap would compile. A function that answers one question
 * about either, at one named site, takes none of that back: it cannot be used
 * to build a row, only to read a string out of one.
 */
export function mapKeyOf(row: EventProjectMap | EventCityMap): string {
  return 'eventKey' in row ? row.eventKey : row.cityKey;
}

/**
 * Whether a key can be sent at all.
 *
 * ⛔ **THE ONLY CLIENT-SIDE CHECK ON A KEY, AND IT IS DELIBERATELY THIS WEAK.**
 * The key belongs to `admin-app`'s vocabulary — underscores on a festival,
 * hyphens on a form region, and neither spelling is this app's to police. A
 * pattern invented here would refuse a key that is correct at their end, with
 * nothing on screen able to explain why, which is a frontend convention dressed
 * as a server rule. **An empty box is the one thing that cannot be right**, and
 * everything else is the server's to refuse in its own words.
 */
export function keyIsEnterable(raw: string): boolean {
  return raw.trim().length > 0;
}
